import { CommonModule } from '@angular/common';
import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder } from '@angular/forms';
import { Store, select } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { NgImageSliderModule } from 'ng-image-slider';
import { BsModalService } from 'ngx-bootstrap/modal';
import { PerfectScrollbarModule } from 'ngx-om-perfect-scrollbar';
import { Observable, mergeMap } from 'rxjs';
import { filter, take } from 'rxjs/operators';

import { ApprovalWorkflowStepsComponent } from '@shared/components/approval-workflow-steps/approval-workflow-steps.component';
import { GenerateApprovalWorkflowModule } from '@shared/components/generate-approval-workflow/generate-approval-workflow.module';
import { HistoryComponent } from '@shared/components/history/history.component';
import { NoteHistoryListComponent } from '@shared/components/note-history/note-history-list.component';
import { ApproveStatusEnum } from '@shared/enum/approve-status.enum';
import { ModalButtonResponseEnum } from '@shared/enum/modal-button-response.enum';
import { NotificationTypeEnum } from '@shared/enum/notification-type.enum';
import { AlertModalComponent } from '@shared/layouts/modals/alert-modal/alert-modal.component';
import { ConfirmModalComponent } from '@shared/layouts/modals/confirm-modal/confirm-modal.component';
import { ConfirmWithMessageModalComponent } from '@shared/layouts/modals/confirm-with-message-modal/confirm-with-message-modal.component';
import { ApprovalFlowResponse } from '@shared/models/approval-flow.model';
import { NotificationEmit } from '@shared/models/notification-emit.model';
import { SharedModule } from '@shared/shared.module';
import { GetApprovalFlowByIdRequest, ResetApprovalFlow } from '@shared/store/actions/approval-flow.actions';
import { selectApprovalFlowResponse } from '@shared/store/selectors/approval-flow.selectors';
import { selectUserInfoResult } from '@shared/store/selectors/auth-user-info.selector';
import { AuthUserInfoState } from '@shared/store/state/auth-user-info.state';
import { currencyUtil } from '@shared/utils/currency-util';

import { environment } from '../../../../../environments/environment';
import { BaseComponent } from '../../../../base/base.component';
import {
  StoreAvailableAdjustmentRequestEnum,
  StoreAvailableAdjustmentRequestRemarkTypeEnum
} from '../../enum/store-available-adjustment-request.enum';
import {
  AdjustItem,
  StoreAvailableAdjustmentRequestViewResponse
} from '../../model/store-available-adjustment-request.model';
import { StoreAvailableAdjustmentPermission } from '../../store-available-adjustment-premission';
import { selectStoreAvailableAdjustmentRequestResponse } from '../../store/store-available-adjustment-request/store-available-adjustment-request.selectors';
import { StoreAvailableAdjustmentRequestViewAction } from '../../store/store-available-adjustment-request/store-available-adjustment.actions';

@Component({
  selector: 'app-store-available-adjustment-request-view',
  templateUrl: './store-available-adjustment-request-view.component.html',
  styleUrls: ['./store-available-adjustment-request-view.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    SharedModule,
    TranslateModule,
    NgImageSliderModule,
    PerfectScrollbarModule,
    GenerateApprovalWorkflowModule,
    ApprovalWorkflowStepsComponent
  ]
})
export class StoreAvailableAdjustmentRequestViewComponent extends BaseComponent implements OnInit, OnDestroy {
  @Output() notifyParent: EventEmitter<NotificationEmit> = new EventEmitter<NotificationEmit>();
  @Output() data: {
    docNo: string;
    title: string;
  };

  storeAvailableAdjustmentView: StoreAvailableAdjustmentRequestViewResponse;
  userInfo: AuthUserInfoState;

  localStore: Observable<any>;

  isShowWorkflow: boolean;
  isShowNewWorkflow: boolean;
  isOldApprovalWorkflowTemplateVersion: boolean;
  isOldTemplateVersion: boolean;
  ApproveStatusEnum = ApproveStatusEnum;
  approvalWorkflow: ApprovalFlowResponse;

  currentPage = 1;
  pageSize = 20;
  pageSizeList = [20, 50, 100];
  dateTimeDisplay = environment.dateTimeDisplay;
  adjustItems: AdjustItem[] = [];
  isViewCancelled = false;
  env = environment;
  canApproveReject = false;
  isRegenerating = false;
  hasCheckedNoteToApprover = false;

  get canRegenerate(): boolean {
    return (
      this.storeAvailableAdjustmentView?.status === StoreAvailableAdjustmentRequestEnum.AWAITING_APPROVAL &&
      this.storeAvailableAdjustmentPermission.hasRegenerateApprovalWorkflow
    );
  }

  get showTotalAmount(): boolean {
    return !!this.storeAvailableAdjustmentView?.totalStoreAvailableAdjustmentAmount;
  }

  get totalAmountExcVat(): number {
    const totalAmount = this.storeAvailableAdjustmentView?.totalStoreAvailableAdjustmentAmount;
    return currencyUtil(totalAmount?.priceIncVat?.amount).subtract(totalAmount?.vat?.amount).value;
  }

  constructor(
    protected readonly store: Store<any>,
    protected readonly modalService: BsModalService,
    protected readonly translate: TranslateService,
    protected readonly storeAvailableAdjustmentPermission: StoreAvailableAdjustmentPermission,
    protected fb: UntypedFormBuilder
  ) {
    super(store, modalService, false);
  }

  ngOnInit(): void {
    this.store.dispatch(StoreAvailableAdjustmentRequestViewAction.request({ requestNo: this.data.docNo }));
    this.localStore = this.store.pipe(untilComponentDestroyed(this));

    this.initialState();
  }

  initialState(): void {
    this.localStore
      .pipe(select(selectUserInfoResult))
      .pipe(
        mergeMap(userInfo => {
          this.userInfo = userInfo;
          return this.localStore.pipe(select(selectStoreAvailableAdjustmentRequestResponse));
        })
      )
      .pipe(filter(result => result !== null))
      .subscribe(value => {
        this.storeAvailableAdjustmentView = value as StoreAvailableAdjustmentRequestViewResponse;
        this.isOldTemplateVersion = !this.storeAvailableAdjustmentView.templateVersion;

        if (this.isOldTemplateVersion) {
          this.canApproveReject = this.isCanApproveReject(
            this.storeAvailableAdjustmentPermission.hasApproveStoreAvailableAdjustment,
            this.storeAvailableAdjustmentView,
            this.userInfo.userNo
          );
        } else {
          this.canApproveReject = this.isCanApproveRejectTemplateVersion2(
            this.storeAvailableAdjustmentPermission.hasApproveStoreAvailableAdjustment,
            this.storeAvailableAdjustmentView,
            this.userInfo.userNo,
            this.userInfo.departmentCode
          );
        }

        this.adjustItems = value.adjustItems;

        // Check note-to-approver only on the first-ever emission (initial page open) --
        // later refetches (regenerate workflow, approve, reject) must not retrigger it,
        // regardless of what canApproveReject evaluates to at that point.
        if (!this.hasCheckedNoteToApprover) {
          this.hasCheckedNoteToApprover = true;
          if (this.canApproveReject && this.storeAvailableAdjustmentView?.notes?.length) {
            this.noteToApproverLog();
          }
        }
      });

    this.localStore.pipe(select(selectApprovalFlowResponse)).subscribe(value => {
      if (value) {
        this.approvalWorkflow = value;
        this.isOldApprovalWorkflowTemplateVersion = !this.approvalWorkflow.templateVersion;
        if (this.isOldApprovalWorkflowTemplateVersion) {
          this.isShowWorkflow = true;
        } else {
          this.isShowNewWorkflow = true;
        }

        if (this.isRegenerating) {
          this.isRegenerating = false;
          this.modalService.show(AlertModalComponent, {
            initialState: {
              title: 'Success',
              message: 'The approval workflow has been regenerated.'
            }
          });
        }
      }
    });
  }

  noteToApproverLog(): void {
    const initialState = {
      title: 'Note to Approver',
      historyHeader: `Request No.: ${this.storeAvailableAdjustmentView.docNo}`,
      detailsList: this.storeAvailableAdjustmentView.notes
    };

    this.modalService.show(NoteHistoryListComponent, {
      initialState
    });
  }

  onShowHistory(): void {
    const initialState = {
      title: 'History',
      historyHeader: `Request No.: ${this.data.docNo}`,
      auditLogs: this.storeAvailableAdjustmentView.auditLogs
    };

    this.modalService.show(HistoryComponent, {
      initialState
    });
  }

  onBack(): void {
    this.isShowWorkflow = false;
    this.isShowNewWorkflow = false;
    this.isRegenerating = false;
  }

  onClose(): void {
    this.notifyParent.emit({ notificationType: NotificationTypeEnum.FORCE_CLOSE });
  }

  doAfterVersionAlertModal() {
    this.notifyParent.emit({ notificationType: NotificationTypeEnum.FORCE_CLOSE });
  }

  doAfterSuccessModal() {
    this.doAfterVersionAlertModal();
  }

  getRemarkMessage(item: AdjustItem) {
    if (!item.remarkType) {
      return '-';
    }
    return item.remarkType === StoreAvailableAdjustmentRequestRemarkTypeEnum.CANCEL_BY_QI
      ? `Cancelled due to re-count by QI Team (${item.remarkRefDoc}) submitted with the same item.`
      : `Cancelled due to re-count by Staff (${item.remarkRefDoc}) submitted with the same item.`;
  }

  onViewApprovalWorkflow(): void {
    if (!this.storeAvailableAdjustmentView.approvalFlowId) {
      this.modalService.show(AlertModalComponent, {
        initialState: {
          title: 'Alert',
          message: 'This request does not have approval workflow.',
          okText: 'OK'
        }
      });
      return;
    }
    this.isRegenerating = false;
    this.store.dispatch(new GetApprovalFlowByIdRequest(this.storeAvailableAdjustmentView.approvalFlowId));
  }

  onRegenerateApprovalWorkflow(): void {
    this.isRegenerating = true;
    this.store.dispatch(
      StoreAvailableAdjustmentRequestViewAction.regenerateWorkflowRequest({
        requestNo: this.storeAvailableAdjustmentView.docNo,
        requestId: this.storeAvailableAdjustmentView.id,
        version: this.storeAvailableAdjustmentView.version
      })
    );
  }

  onViewCancelledItems(): void {
    this.isViewCancelled = !this.isViewCancelled;
    this.isViewCancelled
      ? (this.adjustItems = this.storeAvailableAdjustmentView.adjustItems.filter(
          item => item.status === StoreAvailableAdjustmentRequestEnum.CANCELLED
        ))
      : (this.adjustItems = this.storeAvailableAdjustmentView.adjustItems);
  }

  getColorStatus(status: StoreAvailableAdjustmentRequestEnum): string {
    return status ? StoreAvailableAdjustmentRequestEnum[status].toLowerCase() : '';
  }

  onApprove(): void {
    const cancelledItem = this.adjustItems.filter(
      item => item.status === StoreAvailableAdjustmentRequestEnum.CANCELLED
    );
    if (cancelledItem.length) {
      const alertModalRef = this.modalService.show(ConfirmModalComponent, {
        initialState: {
          title: 'Alert',
          message: `After approving this request, store available stock <br>  for all items will be adjusted, excluding cancelled items. <br> Please note that ${cancelledItem.length} item has been cancelled in this document.`,
          okText: 'OK'
        }
      });
      alertModalRef.content.action.pipe(take(1)).subscribe((result: ModalButtonResponseEnum) => {
        if (result === ModalButtonResponseEnum.OK) {
          this.confirmApprove();
        }
      });
    } else {
      this.confirmApprove();
    }
  }

  confirmApprove() {
    const confirmModalRef = this.modalService.show(ConfirmWithMessageModalComponent, {
      initialState: {
        title: 'Confirm',
        message: 'Are you sure you want to "Approve"?',
        label: 'Comment',
        okText: 'Approve',
        placeholder: 'Comment',
        okClass: 'btn btn-special-approve',
        isRequiredConfirmMessage: false
      }
    });
    confirmModalRef.content.action.pipe(take(1)).subscribe((result: ModalButtonResponseEnum) => {
      if (result === ModalButtonResponseEnum.OK) {
        this.store.dispatch(
          StoreAvailableAdjustmentRequestViewAction.approveRequest({
            payload: {
              docNo: this.data.docNo,
              comment: confirmModalRef.content.confirmMessage,
              version: this.storeAvailableAdjustmentView.version
            }
          })
        );
      }
    });
  }

  onReject(): void {
    const confirmModalRef = this.modalService.show(ConfirmWithMessageModalComponent, {
      initialState: {
        title: 'Confirm',
        message: 'Are you sure you want to "Reject"?',
        label: 'Comment',
        okText: 'Reject',
        placeholder: 'Comment',
        okClass: 'btn btn-special-reject',
        isRequiredConfirmMessage: true
      }
    });
    confirmModalRef.content.action.pipe(take(1)).subscribe((result: ModalButtonResponseEnum) => {
      if (result === ModalButtonResponseEnum.OK) {
        this.store.dispatch(
          StoreAvailableAdjustmentRequestViewAction.rejectRequest({
            payload: {
              docNo: this.data.docNo,
              comment: confirmModalRef.content.confirmMessage,
              version: this.storeAvailableAdjustmentView.version
            }
          })
        );
      }
    });
  }

  hasBaseApprovalRejectAccess(hasPermission: boolean, storeAvailableAdjustmentView: any, userNo: string): boolean {
    return (
      hasPermission &&
      storeAvailableAdjustmentView.status === StoreAvailableAdjustmentRequestEnum.AWAITING_APPROVAL &&
      storeAvailableAdjustmentView.currentApprovals?.includes(userNo)
    );
  }

  isCanApproveReject(hasPermission: boolean, storeAvailableAdjustmentView: any, userNo: string): boolean {
    return this.hasBaseApprovalRejectAccess(hasPermission, storeAvailableAdjustmentView, userNo);
  }

  isCanApproveRejectTemplateVersion2(
    hasPermission: boolean,
    storeAvailableAdjustmentView: any,
    userNo: string,
    departmentCode: string
  ): boolean {
    return (
      this.hasBaseApprovalRejectAccess(hasPermission, storeAvailableAdjustmentView, userNo) &&
      storeAvailableAdjustmentView.currentApprovalDepartmentCode === departmentCode
    );
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.store.dispatch(new ResetApprovalFlow());
    this.store.dispatch(StoreAvailableAdjustmentRequestViewAction.resetView());

    if (this.notifyParent) {
      this.notifyParent.unsubscribe();
    }
  }
  subscribeForVersionError() {}
}
